import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, LogOut, Heart, FileSignature, Sparkles, Wrench, Library, Truck, ReceiptText, PencilRuler } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
// SQUIDDI-ricerca - 2026-10-08
import HeaderSearch from "@/components/HeaderSearch";
import { LOGO } from "@/lib/brand";
import { getMyDrafts } from "@/lib/api";

const NAV = [
  { label: "Catalogo", to: "/shop" },
  { label: "Squiddi Designer", to: "/squiddi-designer" },
  { label: "Lavorazioni", to: "/tecniche" },
  // «Richiedi preventivo» è una richiesta vera, non l'area personale: i miei
  // preventivi e i miei design stanno nel menu dell'account (icona utente).
  { label: "Richiedi preventivo", to: "/richiedi-preventivo" },
  { label: "Contatti", to: "/contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [pendingDrafts, setPendingDrafts] = useState(0);
  const [pendingWcDrafts, setPendingWcDrafts] = useState(0);
  const loc = useLocation();
  const navigate = useNavigate();
  const cart = useCart();
  const { user, logout } = useAuth();

  useEffect(() => { setOpen(false); setAcctOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Bozze in attesa di approvazione (status "inviata"): pallino rosso sull'account.
  useEffect(() => {
    let stopped = false;
    if (!user) { setPendingDrafts(0); setPendingWcDrafts(0); return; }
    const load = async () => {
      try {
        const list = await getMyDrafts();
        if (stopped) return;
        const pending = (list || []).filter((d) => d.status === "inviata");
        setPendingDrafts(pending.length);
        setPendingWcDrafts(pending.filter((d) => d.source === "wecreate").length);
      } catch { if (!stopped) { setPendingDrafts(0); setPendingWcDrafts(0); } }
    };
    load();
    const id = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { stopped = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, loc.pathname]);

  const isActive = (to) => loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_6px_24px_-16px_rgba(16,24,40,.25)]" : "bg-white"}`}>
      <div className="container-x flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 -my-2">
          <img src={LOGO} alt="Squiddi" className="h-10 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => {
            const isWc = n.to === "/squiddi-designer";
            const badgeCount = isWc ? pendingWcDrafts : 0;
            const showWcBadge = isWc && badgeCount > 0;
            return (
              <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative px-4 py-2 rounded-full text-sm font-bold transition-colors ${isActive(n.to) ? "bg-[var(--p-50)] text-[var(--brand)]" : "text-[var(--n-800)] hover:text-[var(--brand)]"}`}>
                {n.label}
                {showWcBadge && (
                  <span data-testid="nav-wecreate-badge" aria-label={`${badgeCount} bozze da approvare`}
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white animate-pulse">
                    {badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1 md:gap-2.5 shrink-0">
          {/* SQUIDDI-ricerca - 2026-10-08 · al posto di «Chiedi a Squiddi»: il
              chatbot si apre dal pallino flottante, qui c'è la ricerca semplice. */}
          <HeaderSearch />
          <button onClick={() => cart?.openCart?.()} data-testid="header-cart" aria-label="Apri il carrello"
            className="relative grid place-items-center w-10 h-10 rounded-full text-[var(--n-800)] hover:text-[var(--brand)] hover:bg-[var(--p-50)] transition-colors">
            <ShoppingBag size={20} />
            {cart?.items?.length > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full bg-[var(--brand)] text-white text-[9px] font-bold" data-testid="cart-badge">{cart.items.length}</span>
            )}
          </button>

          {user && (
            <Link to="/la-mia-libreria" data-testid="header-library" aria-label="La mia libreria"
              className="grid place-items-center w-10 h-10 rounded-full text-black hover:bg-[var(--n-100)] transition-colors">
              <Library size={20} />
            </Link>
          )}

          {user ? (
            <div className="relative">
              <button onClick={() => setAcctOpen((o) => !o)} data-testid="header-account" className="relative grid place-items-center w-10 h-10 rounded-full text-[var(--n-800)] hover:text-[var(--brand)] hover:bg-[var(--p-50)] transition-colors">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <User size={20} />}
                {pendingDrafts > 0 && (
                  <span data-testid="header-drafts-badge" aria-label={`${pendingDrafts} bozze in attesa`}
                    className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white shadow-[0_0_0_1px_rgba(239,68,68,.5)] animate-pulse" />
                )}
              </button>
              {acctOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-2 z-50">
                  <div className="px-3 py-2 border-b border-[var(--n-200)] mb-1">
                    <div className="text-sm font-bold truncate">{user.name}</div>
                    <div className="text-[11px] text-[var(--n-500)] truncate">{user.email}</div>
                  </div>
                  <button onClick={() => { setAcctOpen(false); navigate("/account"); }} data-testid="acct-account" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><User size={15} /> Il mio account</button>
                  {pendingDrafts > 0 && (
                    <button onClick={() => { setAcctOpen(false); navigate("/account?tab=bozze"); }} data-testid="acct-drafts"
                      className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2"><FileSignature size={15} /> Bozze da approvare</span>
                      <span className="min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold">{pendingDrafts}</span>
                    </button>
                  )}
                  <button onClick={() => { setAcctOpen(false); navigate("/account?tab=favorites"); }} data-testid="acct-favorites" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><Heart size={15} /> Preferiti</button>
                  <button onClick={() => { setAcctOpen(false); navigate("/i-miei-ordini"); }} data-testid="acct-orders" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><Truck size={15} /> I miei ordini</button>
                  <button onClick={() => { setAcctOpen(false); navigate("/preventivi"); }} data-testid="acct-quotes" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><ReceiptText size={15} /> I miei preventivi</button>
                  <button onClick={() => { setAcctOpen(false); navigate("/i-miei-lavori"); }} data-testid="acct-projects" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><PencilRuler size={15} /> I miei design</button>
                  <button onClick={() => { setAcctOpen(false); navigate("/la-mia-libreria"); }} data-testid="acct-library" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[var(--n-50)] flex items-center gap-2"><Library size={15} className="text-black" /> La mia libreria</button>
                  <button onClick={() => { setAcctOpen(false); logout(); }} data-testid="acct-logout" className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2"><LogOut size={15} /> Esci</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => setAuthOpen(true)} data-testid="header-login" className="grid place-items-center w-10 h-10 rounded-full text-[var(--n-800)] hover:text-[var(--brand)] hover:bg-[var(--p-50)] transition-colors" aria-label="Accedi"><User size={20} /></button>
          )}

          <button className="lg:hidden grid place-items-center w-10 h-10 rounded-full text-[var(--n-800)]" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--n-300)] bg-white">
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((n) => {
              const isWc = n.to === "/squiddi-designer";
              const badgeCount = isWc ? pendingWcDrafts : 0;
              return (
                <Link key={n.label} to={n.to} className={`flex items-center justify-between py-3 px-4 rounded-xl text-sm font-bold ${isActive(n.to) ? "bg-[var(--p-50)] text-[var(--brand)]" : "text-[var(--n-800)]"}`}>
                  <span>{n.label}</span>
                  {isWc && badgeCount > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold">{badgeCount}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
}
